import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useContent, mediaUrl } from "@/lib/site";
import { MaskLine } from "./Reveal";

const DEFAULT_SLIDES = ["/img/hero.jpg", "/img/p2.jpg", "/img/santorini2.jpg", "/img/p3.jpg"];
const D = sessionStorage.getItem("mx_loaded") ? 0.2 : 2.9;

export const Hero = () => {
  const { t } = useLang();
  const { txt, imgs, raw } = useContent();
  const SLIDES = imgs("hero_images", DEFAULT_SLIDES);
  const video = raw.hero_video ? mediaUrl(raw.hero_video) : "";
  const ref = useRef(null);
  const [i, setI] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % SLIDES.length), 6500);
    return () => clearInterval(id);
  }, [SLIDES.length]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-midnight">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {video && <video src={video} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video" />}
        {!video && SLIDES.map((s, k) => (
          <motion.img key={s} src={s} alt="" initial={false} animate={{ opacity: k === i ? 1 : 0, scale: k === i ? 1.08 : 1 }}
            transition={{ opacity: { duration: 1.6 }, scale: { duration: 8, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover" />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-midnight/60 via-midnight/20 to-midnight/80" />
      <div className="grain absolute inset-0 pointer-events-none" />
      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6" >
        <MaskLine animateNow delay={D} className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.5em] uppercase text-gold mb-8">{txt("heroEyebrow")}</MaskLine>
        <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} className="font-display leading-[0.9]" data-testid="hero-logo">
          <h1 className="sr-only">MEVALUX PROPERTY – {txt("slogan")}</h1>
          <div className="flex justify-center text-[18vw] sm:text-[8rem] lg:text-[10.5rem] tracking-[0.08em]" aria-hidden>
            {"MEVALUX".split("").map((ch, k) => (
              <span key={k} className="overflow-hidden inline-block">
                <motion.span className={`inline-block ${k >= 4 ? "text-gold-shimmer" : "text-ivory"}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
                  transition={{ delay: D + 0.15 + k * 0.07, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>{ch}</motion.span>
              </span>
            ))}
          </div>
          <MaskLine animateNow delay={D + 0.8} className="font-sans font-light text-sm sm:text-xl tracking-[0.9em] pl-[0.9em] text-ivory/90 mt-3">PROPERTY</MaskLine>
        </motion.div>
        <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: D + 1.1, duration: 1.2 }} className="block h-px w-20 bg-gold my-8 origin-center" />
        <MaskLine animateNow delay={D + 1.3} className="font-display italic text-2xl sm:text-4xl text-ivory">{txt("slogan")}</MaskLine>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-ivory/80">
        <span className="text-[10px] tracking-[0.4em] uppercase">{t.scroll}</span>
        <span className="w-px h-12 bg-ivory/20 overflow-hidden"><span className="scroll-dot block w-px h-4 bg-gold" /></span>
      </div>
      <div className={`absolute bottom-8 right-6 lg:right-12 z-10 ${video ? "hidden" : "hidden sm:flex"} items-center gap-5 text-ivory`} data-testid="hero-slider-controls" dir="ltr">
        <button data-testid="hero-prev" onClick={() => setI((i - 1 + SLIDES.length) % SLIDES.length)} className="hover:text-gold transition-colors"><ArrowLeft size={16} strokeWidth={1.2} /></button>
        <span className="font-display text-lg tabular-nums">0{i + 1} <span className="text-ivory/50">/ 0{SLIDES.length}</span></span>
        <button data-testid="hero-next" onClick={() => setI((i + 1) % SLIDES.length)} className="hover:text-gold transition-colors"><ArrowRight size={16} strokeWidth={1.2} /></button>
      </div>
    </section>
  );
};
